import { useState, useMemo } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import Sidebar from '../components/Sidebar';
import Staff from './Staff';
import Settings from './Settings';
import Integrations from './Integrations';
import Products from './Products';
import EditProduct from './EditProduct';
import WalletHistory from './WalletHistory';
import { GET_DASHBOARD_ANALYTICS } from '../graphql/queries';

// ── Helpers ────────────────────────────────────────────────────
function fmt(n, decimals = 2) {
  if (n == null) return '—';
  return Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
}

function fmtDate(ts) {
  if (!ts) return '—';
  const d = new Date(Number(ts));
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateLabel(isoDate) {
  const d = new Date(isoDate + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const STATUS_COLOR = {
  success: '#10b981',
  failed: '#f43f5e',
  pending: '#f59e0b',
  blocked: '#f97316',
  refunded: '#06b6d4',
};

// ── SVG Bar Chart ──────────────────────────────────────────────
function BarChart({ data, loading }) {
  if (loading) {
    return (
      <div className="chart-skeleton-wrap">
        {[60, 90, 50, 120, 80, 100, 70, 40, 110, 75].map((h, i) => (
          <div key={i} className="chart-skeleton-bar" style={{ height: h }} />
        ))}
      </div>
    );
  }
  if (!data || data.length === 0) {
    return <div className="chart-empty-state">No data for selected period</div>;
  }

  const W = 560, H = 210;
  const PAD = { top: 24, bottom: 44, left: 44, right: 12 };
  const cW = W - PAD.left - PAD.right;
  const cH = H - PAD.top - PAD.bottom;

  const max = Math.max(...data.map((d) => d.credits), 0.01);
  const barW = Math.max(6, Math.min(32, (cW / data.length) * 0.55));
  const gap = cW / data.length;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((r) => max * r);
  const labelStep = data.length > 20 ? 4 : data.length > 10 ? 2 : 1;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="bar-chart-svg">
      <defs>
        <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#6366f1" />
          <stop offset="100%" stopColor="#a5b4fc" stopOpacity="0.7" />
        </linearGradient>
      </defs>

      {ticks.map((t, i) => {
        const y = PAD.top + cH - (t / max) * cH;
        return (
          <g key={i}>
            <line
              x1={PAD.left} y1={y} x2={W - PAD.right} y2={y}
              stroke={t === 0 ? '#cbd5e1' : '#e2e8f0'}
              strokeWidth={t === 0 ? 1.5 : 1}
              strokeDasharray={t === 0 ? '0' : '4 3'}
            />
            <text x={PAD.left - 6} y={y + 4} textAnchor="end" fontSize={9} fill="#94a3b8">
              {t < 1 ? t.toFixed(2) : t.toFixed(1)}
            </text>
          </g>
        );
      })}

      {data.map((d, i) => {
        const barH = Math.max(d.credits > 0 ? 3 : 0, (d.credits / max) * cH);
        const x = PAD.left + i * gap + (gap - barW) / 2;
        const y = PAD.top + cH - barH;
        const showLabel = i % labelStep === 0;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={barH} fill="url(#barGrad)" rx={3} />
            {d.credits > 0 && (
              <text x={x + barW / 2} y={y - 5} textAnchor="middle" fontSize={8} fill="#6366f1" fontWeight="600">
                {d.credits.toFixed(1)}
              </text>
            )}
            {showLabel && (
              <text
                x={x + barW / 2} y={H - 10}
                textAnchor="middle" fontSize={9} fill="#94a3b8"
                transform={data.length > 14 ? `rotate(-35, ${x + barW / 2}, ${H - 10})` : undefined}
              >
                {fmtDateLabel(d.date)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ── SVG Donut Chart ────────────────────────────────────────────
function DonutChart({ segments, total, loading }) {
  if (loading) return <div className="donut-skeleton" />;

  const size = 160;
  const cx = size / 2, cy = size / 2;
  const R = 62, r = 40;

  if (total === 0) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={R} fill="none" stroke="#e2e8f0" strokeWidth={R - r} />
        <text x={cx} y={cy - 6} textAnchor="middle" fontSize={18} fontWeight="700" fill="#cbd5e1">0</text>
        <text x={cx} y={cy + 12} textAnchor="middle" fontSize={9} fill="#94a3b8">no data</text>
      </svg>
    );
  }

  let angle = -Math.PI / 2;
  const paths = [];
  segments.forEach((seg) => {
    if (seg.value === 0) return;
    const sweep = (seg.value / total) * 2 * Math.PI;
    const x1 = cx + R * Math.cos(angle);
    const y1 = cy + R * Math.sin(angle);
    angle += sweep;
    const x2 = cx + R * Math.cos(angle);
    const y2 = cy + R * Math.sin(angle);
    const ix1 = cx + r * Math.cos(angle);
    const iy1 = cy + r * Math.sin(angle);
    const ix2 = cx + r * Math.cos(angle - sweep);
    const iy2 = cy + r * Math.sin(angle - sweep);
    const large = sweep > Math.PI ? 1 : 0;
    paths.push({
      ...seg,
      d: `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${R} ${R} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} L ${ix1.toFixed(2)} ${iy1.toFixed(2)} A ${r} ${r} 0 ${large} 0 ${ix2.toFixed(2)} ${iy2.toFixed(2)} Z`,
    });
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {paths.map((p, i) => <path key={i} d={p.d} fill={p.color} />)}
      <text x={cx} y={cy - 6} textAnchor="middle" fontSize={22} fontWeight="700" fill="#1e293b">{total}</text>
      <text x={cx} y={cy + 12} textAnchor="middle" fontSize={9} fill="#64748b">transactions</text>
    </svg>
  );
}

// ── Stat Card ──────────────────────────────────────────────────
function StatCard({ label, value, sub, icon, colorClass, loading }) {
  return (
    <div className={`dash-stat-card ${colorClass}`}>
      <div className="dash-stat-icon-wrap">{icon}</div>
      <div className="dash-stat-content">
        <p className="dash-stat-label">{label}</p>
        <p className="dash-stat-value">
          {loading ? <span className="dash-skeleton-text" /> : value}
        </p>
        {sub && !loading && <p className="dash-stat-sub">{sub}</p>}
      </div>
    </div>
  );
}

// ── Date helpers ───────────────────────────────────────────────
const PRESETS = [
  { label: '7D', days: 7 },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
];

function toIso(date) {
  return date.toISOString().split('T')[0];
}

// ── Dashboard Home ─────────────────────────────────────────────
function DashboardHome({ store }) {
  const today = new Date();
  const [fromDate, setFromDate] = useState(toIso(new Date(today - 30 * 864e5)));
  const [toDate, setToDate] = useState(toIso(today));
  const [activePreset, setActivePreset] = useState('30D');

  const handlePreset = (preset) => {
    setActivePreset(preset.label);
    setFromDate(toIso(new Date(today - preset.days * 864e5)));
    setToDate(toIso(today));
  };

  const { data, loading } = useQuery(GET_DASHBOARD_ANALYTICS, {
    variables: { fromDate, toDate },
    fetchPolicy: 'network-only',
  });

  const analytics = data?.getDashboardAnalytics;
  const wallet = analytics?.wallet;
  const products = analytics?.products;
  const staff = analytics?.staff;
  const creditHistory = analytics?.creditHistory;

  const donutSegments = useMemo(() => {
    if (!creditHistory?.statusBreakdown) return [];
    const map = {};
    creditHistory.statusBreakdown.forEach(({ status, count }) => { map[status] = count; });
    return [
      { label: 'Success', value: map.success || 0, color: STATUS_COLOR.success },
      { label: 'Failed', value: map.failed || 0, color: STATUS_COLOR.failed },
      { label: 'Pending', value: map.pending || 0, color: STATUS_COLOR.pending },
      { label: 'Blocked', value: map.blocked || 0, color: STATUS_COLOR.blocked },
      { label: 'Refunded', value: map.refunded || 0, color: STATUS_COLOR.refunded },
    ];
  }, [creditHistory]);

  const todayStr = toIso(today);

  return (
    <div className="analytics-page">

      {/* ── Header ── */}
      <header className="analytics-header">
        <div>
          <h1 className="dashboard-title">Analytics</h1>
          <p className="dashboard-subtitle">{store.storeName} — Store Overview</p>
        </div>
        <div className="analytics-controls">
          <div className="date-presets">
            {PRESETS.map((p) => (
              <button
                key={p.label}
                className={`preset-btn${activePreset === p.label ? ' preset-btn--active' : ''}`}
                onClick={() => handlePreset(p)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="date-range-wrap">
            <div className="date-field">
              <span className="date-field-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </span>
              <input
                type="date"
                className="date-input"
                value={fromDate}
                max={toDate}
                onChange={(e) => { setFromDate(e.target.value); setActivePreset(''); }}
              />
            </div>
            <span className="date-sep">→</span>
            <div className="date-field">
              <span className="date-field-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </span>
              <input
                type="date"
                className="date-input"
                value={toDate}
                min={fromDate}
                max={todayStr}
                onChange={(e) => { setToDate(e.target.value); setActivePreset(''); }}
              />
            </div>
          </div>
        </div>
      </header>

      {/* ── Stat Cards ── */}
      <div className="dash-stats-grid">
        <StatCard
          label="Available Credits"
          value={wallet ? fmt(wallet.availableCredits) : '—'}
          sub={wallet ? `${fmt(wallet.blockedCredits)} blocked` : null}
          loading={loading}
          colorClass="dash-stat--green"
          icon={
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        <StatCard
          label="Credits Used"
          value={creditHistory ? fmt(creditHistory.totalCreditsUsed) : '—'}
          sub={creditHistory ? `${creditHistory.transactionCount} transactions` : null}
          loading={loading}
          colorClass="dash-stat--indigo"
          icon={
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          }
        />
        <StatCard
          label="Total Products"
          value={products ? fmt(products.total, 0) : '—'}
          sub={products ? `${fmt(products.active, 0)} active · ${fmt(products.draft, 0)} draft` : null}
          loading={loading}
          colorClass="dash-stat--violet"
          icon={
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          }
        />
        <StatCard
          label="Total Staff"
          value={staff ? fmt(staff.total, 0) : '—'}
          loading={loading}
          colorClass="dash-stat--amber"
          icon={
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          }
        />
      </div>

      {/* ── Charts Row ── */}
      <div className="analytics-charts-row">

        {/* Bar Chart */}
        <div className="chart-card chart-card--large">
          <div className="chart-card-header">
            <div>
              <h2 className="chart-card-title">Daily Credit Usage</h2>
              <p className="chart-card-sub">Credits consumed per day in selected period</p>
            </div>
            {loading && <div className="loader-ring loader-ring--sm" />}
          </div>
          <div className="bar-chart-wrap">
            <BarChart data={creditHistory?.dailyUsage ?? []} loading={loading} />
          </div>
        </div>

        {/* Donut Chart */}
        <div className="chart-card chart-card--small">
          <div className="chart-card-header">
            <div>
              <h2 className="chart-card-title">Status Breakdown</h2>
              <p className="chart-card-sub">Transactions by status</p>
            </div>
          </div>
          <div className="donut-chart-wrap">
            <DonutChart
              segments={donutSegments}
              total={creditHistory?.transactionCount ?? 0}
              loading={loading}
            />
            <div className="donut-legend">
              {donutSegments.filter((s) => s.value > 0).map((s) => (
                <div key={s.label} className="donut-legend-item">
                  <span className="donut-legend-dot" style={{ background: s.color }} />
                  <span className="donut-legend-label">{s.label}</span>
                  <span className="donut-legend-count">{s.value}</span>
                </div>
              ))}
              {!loading && (creditHistory?.transactionCount ?? 0) === 0 && (
                <p className="donut-empty-text">No transactions in period</p>
              )}
            </div>
          </div>
        </div>

      </div>

      {/* ── Recent Transactions ── */}
      <div className="analytics-table-card">
        <div className="chart-card-header">
          <div>
            <h2 className="chart-card-title">Recent Transactions</h2>
            <p className="chart-card-sub">Latest credit operations in selected period</p>
          </div>
        </div>

        {loading ? (
          <div className="staff-loading"><div className="loader-ring" /></div>
        ) : (creditHistory?.recentTransactions ?? []).length === 0 ? (
          <div className="analytics-empty-state">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p>No transactions found in this period</p>
          </div>
        ) : (
          <div className="staff-table-wrap">
            <table className="staff-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Operation</th>
                  <th>Staff</th>
                  <th style={{ textAlign: 'right' }}>Credits Used</th>
                  <th style={{ textAlign: 'right' }}>Balance After</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {(creditHistory?.recentTransactions ?? []).map((h) => (
                  <tr key={h._id}>
                    <td style={{ whiteSpace: 'nowrap', fontSize: '0.82rem' }}>{fmtDate(h.creationDate)}</td>
                    <td>
                      <span style={{ fontSize: '0.8rem', fontWeight: 500, textTransform: 'capitalize' }}>
                        {h.operationType?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td>
                      {h.staffName ? (
                        <div className="staff-name-cell">
                          <div className="staff-avatar" style={{ width: 26, height: 26, fontSize: '0.72rem' }}>
                            {h.staffName.charAt(0).toUpperCase()}
                          </div>
                          <span>{h.staffName}</span>
                        </div>
                      ) : (
                        <span className="text-placeholder">—</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {h.creditsUsed ?? '—'}
                    </td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {h.availableCreditsAfterOperation ?? '—'}
                    </td>
                    <td>
                      <span
                        className="analytics-status-pill"
                        style={{
                          background: `${STATUS_COLOR[h.status] || '#94a3b8'}18`,
                          color: STATUS_COLOR[h.status] || '#64748b',
                          borderColor: `${STATUS_COLOR[h.status] || '#94a3b8'}40`,
                        }}
                      >
                        {h.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Bottom Row: Store Info + Products Summary ── */}
      <div className="analytics-bottom-row">

        <div className="analytics-info-card">
          <div className="chart-card-header">
            <h2 className="chart-card-title">Store Details</h2>
            <div className="store-verified-chip">
              <span className="store-info-chip-dot" />
              Verified
            </div>
          </div>
          <div className="store-info-list">
            <div className="store-info-item">
              <span className="store-info-item-label">Store Name</span>
              <span className="store-info-item-value">{store.storeName}</span>
            </div>
            <div className="store-info-item">
              <span className="store-info-item-label">Store ID</span>
              <span className="store-info-item-value store-info-mono">{store.storeId}</span>
            </div>
            <div className="store-info-item">
              <span className="store-info-item-label">Client ID</span>
              <span className="store-info-item-value store-info-mono">{store.clientId}</span>
            </div>
            <div className="store-info-item">
              <span className="store-info-item-label">Status</span>
              <span className="analytics-status-pill" style={{ background: '#10b98118', color: '#10b981', borderColor: '#10b98140' }}>
                Active
              </span>
            </div>
          </div>
        </div>

        <div className="analytics-info-card">
          <div className="chart-card-header">
            <h2 className="chart-card-title">Products Summary</h2>
          </div>
          <div className="products-summary-grid">
            <div className="products-summary-item products-summary-item--total">
              <span className="products-summary-value">{loading ? '—' : fmt(products?.total ?? 0, 0)}</span>
              <span className="products-summary-label">Total</span>
            </div>
            <div className="products-summary-item products-summary-item--active">
              <span className="products-summary-value">{loading ? '—' : fmt(products?.active ?? 0, 0)}</span>
              <span className="products-summary-label">Active</span>
            </div>
            <div className="products-summary-item products-summary-item--draft">
              <span className="products-summary-value">{loading ? '—' : fmt(products?.draft ?? 0, 0)}</span>
              <span className="products-summary-label">Draft</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default function Dashboard({ store }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const activePage = pathname.replace(/^\//, '') || 'dashboard';

  return (
    <div className="dashboard">
      <Sidebar
        storeName={store.storeName}
        activePage={activePage}
        onNavigate={(page) => navigate(`/${page}`)}
      />
      <div className="dashboard-content">
        <Routes>
          <Route path="/dashboard" element={<DashboardHome store={store} />} />
          <Route path="/staff" element={<Staff />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/integrations" element={<Integrations />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:id/edit" element={<EditProduct />} />
          <Route path="/wallet-history" element={<WalletHistory />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </div>
    </div>
  );
}
